import React from "react"
import { Redirect, useLocation } from "react-router-dom"

/**
 * The pre-rename `/consumer/subscription/*` → `/consumer/payment/*` (CONAPP-385).
 *
 * Mounted as a PREFIX route in `Root`, so it catches the funnel root and every step beneath it and
 * forwards the tail unchanged — one component instead of ten `<Redirect from to>` pairs.
 *
 * The query string and hash are carried over because react-router's string `to` drops them, and on
 * this path the query is the whole point: the checkout step builds Stripe's `return_url` from
 * `window.location.origin` plus the confirmation path, so a card that needs a full-page 3DS redirect
 * comes back to whichever path was live when the payment started. A payment begun moments before
 * this rename ships returns to the OLD confirmation url with `payment_intent_client_secret`
 * attached, and never finalizes if the redirect loses it.
 *
 * The funnel is also public and linked from marketing surfaces outside this repo (and from
 * `public/consumer-landing.html`), so old links stay in circulation regardless of Stripe.
 *
 * Slicing by prefix LENGTH rather than matching the string keeps this correct for the mixed-case
 * urls react-router also accepts here (`/consumer/SUBSCRIPTION/checkout`): the route matches
 * case-insensitively, and the tail is preserved verbatim for the funnel's own case-insensitive
 * step matching (see `subscription-steps.ts`).
 *
 * Same pattern as `#/pages/consumer-subscription-billing/LegacyBillingPathRedirect` (the
 * `/consumer/subscription-billing` → `/consumer/billing-detail` rename).
 */
const LEGACY_FUNNEL_PATH = "/consumer/subscription"
const FUNNEL_PATH = "/consumer/payment"

export default function LegacyFunnelPathRedirect(): JSX.Element {
  const { pathname, search, hash } = useLocation()
  const tail = pathname.slice(LEGACY_FUNNEL_PATH.length)
  return <Redirect to={{ pathname: `${FUNNEL_PATH}${tail}`, search, hash }} />
}
